/**
 * The decomposition wizard (PRD B1b, G7).
 *
 * A structured session is a server-driven loop where the current item is
 * re-issued until it is answered. Forcing that into a message transcript is
 * exactly the mismatch that made the extension tiring, so this is a wizard: one
 * item, unmissable, with progress visible and prior answers reachable.
 *
 * Chat stays available underneath for thinking out loud, but it is never the
 * primary control.
 */

import { useCallback, useEffect, useState } from 'react';
import type { AnsweredItem, StructuredItem, SubmitResult } from '@chat42/runtime';


interface WizardState {
  done: boolean;
  current?: StructuredItem;
  draft: string;
  answered: AnsweredItem[];
  errors?: SubmitResult['errors'];
}

export interface WizardProps {
  provider: string;
  target: string;
  /** Rendered under the item, for thinking out loud. Never the primary control. */
  discussion?: React.ReactNode;
}

export function StructuredWizard({ provider, target, discussion }: WizardProps) {
  const [state, setState] = useState<WizardState>();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPrior, setShowPrior] = useState(false);
  const [error, setError] = useState<string>();

  const base = `/api/structured/${encodeURIComponent(provider)}/${encodeURIComponent(target)}`;

  const post = useCallback(
    async (path: string, body?: unknown) => {
      const res = await fetch(`${base}${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) throw new Error(`The wizard could not reach the session (${res.status}).`);
      return (await res.json()) as WizardState & { result?: SubmitResult };
    },
    [base],
  );

  useEffect(() => {
    let cancelled = false;
    void post('')
      .then((s) => {
        if (cancelled) return;
        setState(s);
        // The draft the agent proposed, pre-filled and editable — which is the
        // difference between reviewing and authoring.
        setDraft(s.draft);
      })
      .catch((e: Error) => setError(e.message));
    return () => { cancelled = true; };
  }, [post]);

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await post('/draft', { payload: draft });
      const next = await post('/submit');
      setState(next);
      setDraft(next.draft);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="c42-page"><div className="c42-banner">{error}</div></div>;
  if (!state) return <div className="c42-page"><div className="c42-empty">Opening…</div></div>;

  if (state.done) {
    return (
      <div className="c42-page"><div className="c42-pwrap">
        <div className="c42-phd"><h2>Done</h2>
          <p>{state.answered.length} items answered.</p></div>
        <AnsweredList answered={state.answered} />
      </div></div>
    );
  }

  const item = state.current!;
  const pos = item.position;

  return (
    <div className="c42-page"><div className="c42-pwrap">
      <div className="c42-phd">
        <h2>{item.title}</h2>
        {/* Progress visible, because a loop with no end in sight reads as a
            transcript again. */}
        {pos && <p>Item {pos.index} of {pos.total}</p>}
      </div>

      {pos && (
        <div className="c42-slots" style={{ marginBottom: 16 }}>
          {Array.from({ length: pos.total }, (_, i) => (
            <i key={i} className={`c42-slot${i < pos.index - 1 ? ' on' : ''}`} />
          ))}
        </div>
      )}

      <div className="c42-list">
        <div className="c42-item">
          <p className="c42-why" style={{ whiteSpace: 'pre-wrap', marginTop: 0 }}>{item.prompt}</p>
        </div>
        <div className="c42-item">
          <div className="c42-ctitle">Answer — expected shape</div>
          <p className="c42-why mono">{item.submitShape}</p>
          <textarea
            className="c42-wizard-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={8}
            spellCheck={false}
            style={{
              width: '100%', marginTop: 8, font: 'inherit',
              fontFamily: 'var(--c42-font-mono)', fontSize: 12,
              border: '1px solid var(--c42-line)', borderRadius: 'var(--c42-radius)',
              padding: 8, resize: 'vertical', background: 'var(--c42-paper)',
              color: 'var(--c42-ink)',
            }}
          />
          {state.errors?.length ? (
            // The server does not advance on a bad submit, so the wizard must
            // say what was wrong rather than looking stuck.
            <div className="c42-banner" style={{ marginTop: 8 }}>
              {state.errors.map((e) => `${e.field === '_' ? '' : `${e.field}: `}${e.message}`).join('\n')}
            </div>
          ) : null}
          <div className="c42-acts">
            <button className="c42-mini primary" disabled={busy || !draft.trim()} onClick={() => void submit()}>
              Submit and continue
            </button>
            {state.answered.length > 0 && (
              <button className="c42-mini" onClick={() => setShowPrior((v) => !v)}>
                {showPrior ? 'Hide' : `Prior answers (${state.answered.length})`}
              </button>
            )}
          </div>
        </div>
      </div>

      {showPrior && <AnsweredList answered={state.answered} />}

      {discussion && (
        <>
          <div className="c42-gl"><h3>Discuss this item</h3><span className="rule" /></div>
          {discussion}
        </>
      )}
    </div></div>
  );
}

function AnsweredList({ answered }: { answered: AnsweredItem[] }) {
  if (!answered.length) return null;
  return (
    <>
      <div className="c42-gl"><h3>Answered</h3><span className="rule" /></div>
      <div className="c42-list">
        {answered.map((a) => (
          <div className="c42-item" key={a.key}>
            <div className="c42-top"><b>{a.title}</b></div>
            <p className="c42-why mono" style={{ whiteSpace: 'pre-wrap' }}>{a.payload}</p>
          </div>
        ))}
      </div>
    </>
  );
}
